import type { UsedInOptions } from "@circle-react/contexts/currentPostContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { GalleryImage } from "@circle-react/types/GalleryImage";
import { useRef } from "react";

import { DeletedImagePlaceholder } from "./DeletedImagePlaceholder";
import { resolvePositionClassName } from "./helpers";
import { useContainerCalculatedHeight } from "./hooks/useContainerCalculatedHeight";
import type { ZoomLevels } from "./hooks/useZoom";
import { Image } from "./Image";
import { NavigationArrow } from "./NavigationArrow";

interface ImagesProps {
  currentImageIndex: number;
  images: GalleryImage[];
  onNextImage: () => void;
  onPrevImage: () => void;
  shouldAnimate: boolean;
  shouldHaveRoundedBottomCorners?: boolean;
  usedIn: UsedInOptions;
  zoomLevel: ZoomLevels;
  zoomPosition: [number, number];
}
export const Images = ({
  images,
  currentImageIndex,
  onPrevImage,
  onNextImage,
  usedIn,
  shouldAnimate,
  zoomLevel,
  zoomPosition,
  shouldHaveRoundedBottomCorners = false,
}: ImagesProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Use square aspect ratio (1:1) as default when images are deleted
  const galleryAspectRatio =
    images.length > 0 ? images[0].width / images[0].height : 1;

  const { containerRatio, containerCalculatedHeight, containerWidth } =
    useContainerCalculatedHeight(galleryAspectRatio, containerRef, usedIn);
  const positionClassName = resolvePositionClassName(
    galleryAspectRatio,
    containerRatio,
  );

  return (
    <div
      className={classNames(
        "group relative flex items-center overflow-hidden",
        {
          "lg:h-full": usedIn === "full-post-modal",
          "md:rounded-b-md": shouldHaveRoundedBottomCorners,
        },
      )}
      style={{
        height:
          usedIn !== "full-post-modal" ? containerCalculatedHeight : undefined,
      }}
      ref={containerRef}
    >
      {images.length > 0 ? (
        images.map((image, index) => (
          <Image
            key={image.id}
            image={image}
            currentImageIndex={currentImageIndex}
            index={index}
            positionClassName={positionClassName}
            containerWidth={containerWidth}
            usedIn={usedIn}
            shouldAnimate={shouldAnimate}
            zoomLevel={zoomLevel}
            zoomPosition={zoomPosition}
          />
        ))
      ) : (
        <DeletedImagePlaceholder positionClassName={positionClassName} />
      )}
      {images.length > 0 && currentImageIndex > 0 && (
        <NavigationArrow onClick={onPrevImage} direction="prev" />
      )}
      {images.length > 0 && currentImageIndex < images.length - 1 && (
        <NavigationArrow onClick={onNextImage} direction="next" />
      )}
    </div>
  );
};
